import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { Search, Plus, Archive, Bell, Users } from 'lucide-react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Avatar } from '../ui/avatar'
import { Switch } from '../ui/switch'
import { formatMessageTime } from '../../utilities/messageHelpers'
import LoadingConversations from './LoadingConversations'
import EmptyConversationState from './EmptyConversationState'
import ArchivedConversationsModal from './ArchivedConversationsModal'

function ConversationList({
  conversations,
  groupConversations,
  selectedConversationId,
  selectedGroupConversationId,
  onSelectConversation,
  onSelectGroupConversation,
  onNewConversation,
  onNewGroupConversation,
  isLoading = false,
  currentUser,
  fetchArchivedConversations,
  onRestoreConversation
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isUpdatingNotifications, setIsUpdatingNotifications] = useState(false)
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true)
  const [showArchivedModal, setShowArchivedModal] = useState(false)

  const canCreateGroup =
    currentUser?.superuser ||
    currentUser?.coach ||
    currentUser?.facilitiesUsers?.some((fu) => fu.admin)

  useEffect(() => {
    if (currentUser?.attributes?.emailMessageNotifications !== undefined) {
      setEmailNotificationsEnabled(currentUser.attributes.emailMessageNotifications)
    }
  }, [currentUser])

  const allItems = [
    ...conversations.map((c) => ({ ...c, conversationType: 'direct' })),
    ...groupConversations.map((gc) => ({ ...gc, conversationType: 'group' }))
  ].sort((a, b) => {
    const aTime = new Date(a.attributes?.lastMessageAt || a.attributes?.createdAt || 0)
    const bTime = new Date(b.attributes?.lastMessageAt || b.attributes?.createdAt || 0)
    return bTime - aTime
  })

  const filteredItems = allItems.filter((item) => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    if (item.conversationType === 'group') {
      return (item.attributes?.title || '').toLowerCase().includes(term)
    }
    const other = item.attributes?.otherParticipant
    const name = other?.preferredName || other?.fullName || ''
    return name.toLowerCase().includes(term)
  })

  const handleToggleEmailNotifications = async (checked) => {
    if (!currentUser?.id) return

    setEmailNotificationsEnabled(checked)
    setIsUpdatingNotifications(true)
    try {
      await axios.patch(`/api/users/${currentUser.id}`, {
        user: {
          email_message_notifications: checked
        }
      })
    } catch (error) {
      console.error('Error updating notification settings:', error)
      setEmailNotificationsEnabled(!checked)
    } finally {
      setIsUpdatingNotifications(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-lg font-semibold text-gray-900 flex-1">Messages</h2>
          {canCreateGroup && (
            <Button
              onClick={onNewGroupConversation}
              size="icon"
              variant="outline"
              className="h-9 w-9"
              aria-label="New group message"
            >
              <Users className="w-4 h-4" />
            </Button>
          )}
          <Button onClick={onNewConversation} size="icon" variant="outline" className="h-9 w-9" aria-label="New message">
            <Plus className="w-5 h-5" />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <LoadingConversations />
        ) : allItems.length === 0 ? (
          <EmptyConversationState />
        ) : filteredItems.length === 0 ? (
          <div className="flex items-center justify-center h-full p-8 text-center">
            <p className="text-sm text-gray-500">No conversations found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredItems.map((item) => {
              const attrs = item.attributes || {}
              const unreadCount = attrs.unreadCount || 0
              const isGroup = item.conversationType === 'group'

              if (isGroup) {
                const isSelected = item.id === selectedGroupConversationId
                return (
                  <button
                    key={`group-${item.id}`}
                    onClick={() => onSelectGroupConversation(item.id)}
                    className={`w-full p-4 text-left transition-colors hover:bg-gray-50 ${isSelected ? 'bg-purple-50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="size-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="font-medium truncate text-gray-900">{attrs.title}</p>
                            <Badge variant="secondary" className="bg-purple-50 text-purple-600 border-purple-200 text-[10px] px-1.5 py-0 flex-shrink-0">
                              Group
                            </Badge>
                          </div>
                          <span className="text-xs text-gray-500 flex-shrink-0 ml-2 tabular-nums">
                            {formatMessageTime(attrs.lastMessageAt)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm truncate text-gray-500 text-pretty">
                            {attrs.lastMessagePreview || `${attrs.participantCount} participants`}
                          </p>
                          {unreadCount > 0 && <Badge className="ml-2 flex-shrink-0 bg-purple-600 tabular-nums">{unreadCount}</Badge>}
                        </div>
                      </div>
                    </div>
                  </button>
                )
              }

              const otherParticipant = attrs.otherParticipant || {}
              const isSelected = item.id === selectedConversationId
              const isBlocked = attrs.isBlocked || false

              const buttonClasses = isBlocked
                ? 'w-full p-4 text-left transition-colors bg-red-100 hover:bg-red-50'
                : `w-full p-4 text-left transition-colors hover:bg-gray-50 ${isSelected ? 'bg-purple-50' : ''}`

              return (
                <button
                  key={`direct-${item.id}`}
                  onClick={() => onSelectConversation(item.id)}
                  className={buttonClasses}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="w-12 h-12 flex-shrink-0">
                      {otherParticipant.cloudinaryPhotoPublicId ? (
                        <img
                          src={`https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,g_face,h_48,w_48/${otherParticipant.cloudinaryPhotoPublicId}.jpg`}
                          alt={otherParticipant.preferredName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold">
                          {(otherParticipant.preferredName || otherParticipant.fullName || '?')[0].toUpperCase()}
                        </div>
                      )}
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <p className={`font-medium truncate ${isBlocked ? 'text-red-700' : 'text-gray-900'}`}>
                          {otherParticipant.preferredName || otherParticipant.fullName}
                          {isBlocked && <span className="ml-2 text-xs font-normal text-red-600">(Blocked)</span>}
                        </p>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2 tabular-nums">
                          {formatMessageTime(attrs.lastMessageAt)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className={`text-sm truncate text-pretty ${isBlocked ? 'text-red-600' : 'text-gray-500'}`}>
                          {attrs.lastMessagePreview || 'No messages yet'}
                        </p>
                        {unreadCount > 0 && <Badge className="ml-2 flex-shrink-0 bg-purple-600 tabular-nums">{unreadCount}</Badge>}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-2">
        <div className="flex items-center justify-between py-1.5 px-3">
          <div className="flex items-center gap-3">
            <Bell className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm text-gray-700">Email notifications</span>
          </div>
          <Switch
            checked={emailNotificationsEnabled}
            onCheckedChange={handleToggleEmailNotifications}
            disabled={isUpdatingNotifications}
          />
        </div>

        <button
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          onClick={() => setShowArchivedModal(true)}
        >
          <Archive className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span>Archived conversations</span>
        </button>
      </div>

      <ArchivedConversationsModal
        isOpen={showArchivedModal}
        onClose={() => setShowArchivedModal(false)}
        fetchArchivedConversations={fetchArchivedConversations}
        onRestore={onRestoreConversation}
      />
    </div>
  )
}

ConversationList.propTypes = {
  conversations: PropTypes.array.isRequired,
  groupConversations: PropTypes.array,
  selectedConversationId: PropTypes.string,
  selectedGroupConversationId: PropTypes.string,
  onSelectConversation: PropTypes.func.isRequired,
  onSelectGroupConversation: PropTypes.func,
  onNewConversation: PropTypes.func.isRequired,
  onNewGroupConversation: PropTypes.func,
  isLoading: PropTypes.bool,
  currentUser: PropTypes.object,
  fetchArchivedConversations: PropTypes.func.isRequired,
  onRestoreConversation: PropTypes.func.isRequired
}

ConversationList.defaultProps = {
  groupConversations: [],
  onSelectGroupConversation: () => {},
  onNewGroupConversation: () => {}
}

export default ConversationList
